import { getConfigs } from "../configs/Config";
import {
  setApplicantAppFeePayment,
  setApplicationTermsAndConditions,
  setNotification,
  setInitialFilterOptions,
  setApplications,
  setApplication,
  setConfiguration,
  setApplicationPaymentDetails,
  setCommsPreferences,
  resolveIssueSuccess,
} from "src/store/features/applications/applicationsSlice";
import { getHelpRequestsBulk } from "./HelpRequestService";
import { fetchAccessToken, fetchIdToken, decodeToken } from "../utils/Auth";
import { chunk } from "../utils/Utils";
import LocalStorageService from "./LocalStorageService";
import { AddendumsPost, AddendumsPostAddendums } from "src/types/Addendum";
import { mergeAddendumFormsAndResponses } from "src/utils/Addendum";
import {
  LeaseEventId,
  PostLeaseResponse,
  Application,
  GetApplicationsBody,
} from "src/types/Application";
import { GetFilterOptionsBody } from "src/types/FilterOptions";
import { AppThunk } from "src/redux/store";
import { formatArrayToString } from "src/utils/Filter";
import { PaymentType, type CommunityPaymentsResponse } from "src/types/Payment";
import {
  ApplicationConfiguration,
  CommsPreference,
  Features,
} from "src/types/Redux";
import { Applicant, ScreeningVendor } from "src/types/Applicant";

export const getApplications = (communityIds: string[] = []): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();

  const route = `${baseRoute}/applications/search`;

  return (dispatch) => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", fetchIdToken() || "");
    headers.append("x-access-token", fetchAccessToken() || "");
    headers.append("x-api-key", apiKey || "");
    const options = {
      method,
      headers,
      body: JSON.stringify({
        communityIds: communityIds,
      }),
    };
    fetch(route, options)
      .then((data) => data.json())
      .then((res: GetApplicationsBody) => {
        if (res && res.applications) {
          const applicationIds = res.applications.map(
            (application: Application) => {
              return application.applicationId;
            }
          );

          const applicationIdChunks = chunk(applicationIds, 100);
          applicationIdChunks.map((group) => {
            dispatch(getHelpRequestsBulk(group));
          });
        }

        dispatch(setApplications(res));
      })
      .catch((error) => {
        console.log("error on getting applications ", error);
        dispatch(setApplications({ applications: [], count: 0 }));
      });
  };
};

export const getApplicationById = (applicationId): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();
  const route = `${baseRoute}/applications/${applicationId}?includeDeletedApplicants=true`;

  return (dispatch) => {
    const method = "GET";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", fetchIdToken() || "");
    headers.append("x-api-key", apiKey || "");
    headers.append("x-access-token", fetchAccessToken() || "");
    const options = {
      method,
      headers,
    };
    fetch(route, options)
      .then((data) => data.json())
      .then((res) => {
        const { application: applicationFromLocal } =
          LocalStorageService.getApplication(applicationId);

        const application = {
          ...applicationFromLocal,
          ...res,
        };

        dispatch(setApplication(application));
        LocalStorageService.setApplicationInLocal(
          { ...application, lastFetchedDate: new Date().toJSON() },
          null
        );
      })
      .catch((error) => {
        console.log(
          `error on getting application by id - ${applicationId}`,
          error
        );
      });
  };
};

export const getFilterOptions = (): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();

  const route = `${baseRoute}/filter-options`;

  return (dispatch) => {
    const method = "GET";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", fetchIdToken() || "");
    headers.append("x-api-key", apiKey || "");

    const options = {
      method,
      headers,
    };

    fetch(route, options)
      .then((data) => data.json())
      .then((res: GetFilterOptionsBody) => {
        dispatch(setInitialFilterOptions(res));
      })
      .catch((error) => {
        console.log("error on getting initial filter options ", error);
      });
  };
};

export const updateUserFilterSelection = (selectedOptions): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();

  const route = `${baseRoute}/filter-options`;

  return () => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", fetchIdToken() || "");
    headers.append("x-api-key", apiKey || "");
    const body = {
      regions: formatArrayToString(selectedOptions.regions),
      markets: formatArrayToString(selectedOptions.markets),
      communities: formatArrayToString(selectedOptions.communities),
    };
    const options = {
      method,
      headers,
      body: JSON.stringify(body),
    };
    fetch(route, options)
      .then((data) => data.json())
      .then(() => {
        /**
         * No need to do anything here. Reaching this point
         * simply means that the new filter selections were saved.
         */
      })
      .catch((error) => {
        console.log("Error on getting initial filter options ", error);
      });
  };
};

export const getApplicantPaymentStatus = (
  applicationId: string,
  applicantId: string
): AppThunk => {
  return getCommunityApplicantPaymentStatus(applicationId, applicantId);
};

export const getCommunityApplicantPaymentStatus = (
  applicationId: string,
  applicantId: string
): AppThunk => {
  return async (dispatch) => {
    try {
      const { apiKey, baseRoute } = getConfigs();
      const accessToken = sessionStorage.getItem("access_token") || "";

      const route = `${baseRoute}/applications/${applicationId}/payments`;
      const headers = new Headers({
        "Content-type": "application/json",
        Accept: "application/json",
        "x-api-key": apiKey || "",
        "x-access-token": accessToken,
      });

      const response = await fetch(route, { method: "GET", headers });
      const data: CommunityPaymentsResponse = await response.json();

      if (applicantId) {
        // If applicantId is present, we're specifically checking Application Fee payment status
        // for that applicant. Application Fees are tied to individual applicants -- multiple
        // applicants may have their own separate Application Fee payments -- so we filter by
        // payments that reference this applicantId.
        const appFee = data?.paymentAmounts?.applicationFee;

        let paymentStatus: string | undefined;

        if (appFee === 0) {
          paymentStatus = "Not Applicable";
        } else {
          paymentStatus =
            data?.payments
              ?.sort(
                (a, b) =>
                  new Date(b.createdDate).getTime() -
                  new Date(a.createdDate).getTime()
              )
              ?.find(
                (payment) =>
                  payment.paymentType === PaymentType.ApplicationFee &&
                  payment.references.some(
                    (ref) =>
                      ref.refKey === "ApplicantIds" &&
                      ref.refValue.includes(String(applicantId))
                  )
              )?.paymentStatus || undefined;
        }

        dispatch(setApplicantAppFeePayment(paymentStatus));
      } else {
        // If applicantId is NOT present, we assume the intent is to fetch Security Deposit or
        // Increased Security Deposit payment data. These payment types *can* be tied to applicants
        // (and include ApplicantIds), but for this use case - which is showing the security deposit
        // info at the application level - we don't need to scope by applicant.
        //
        // So we store the full response and let the consuming component derive what it needs.
        dispatch(
          setApplicationPaymentDetails({
            payments: data.payments,
            paymentAmounts: data.paymentAmounts,
          })
        );
      }
    } catch (error) {
      console.error(
        "Error fetching community applicant payment status:",
        error
      );
    }
  };
};

export const getApplicationTermsAndConditions = (applicantId): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();
  const route = `${baseRoute}/terms-and-conditions/${applicantId}`;

  return (dispatch) => {
    const method = "GET";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", sessionStorage.getItem("token") || "");
    headers.append("x-api-key", apiKey || "");
    const options = {
      method,
      headers,
    };
    fetch(route, options)
      .then((data) => data.json())
      .then((res) => {
        dispatch(setApplicationTermsAndConditions(res));
      })
      .catch((error) => {
        console.log("Error on getting application documents ", error);
      });
  };
};

export const cancelApplication = (
  applicationId,
  unitId,
  cancelledBy: number | undefined = undefined,
  selectedTemplate: string | undefined = undefined
): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();
  const route = `${baseRoute}/cancel-application`;
  return (dispatch) => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", sessionStorage.getItem("token") || "");
    headers.append("x-api-key", apiKey || "");
    const body = {
      applicationId: applicationId,
      cancelledBy: cancelledBy,
      cancellationTemplate: selectedTemplate,
    };
    const options = {
      method,
      headers,
      body: JSON.stringify(body),
    };
    fetch(route, options)
      .then((response) => {
        if (!response.ok) {
          return Promise.reject(Error);
        }
        dispatch(
          setNotification({
            type: "INFO",
            message: `Application for Unit ${unitId} has been successfully cancelled`,
            origin: "cancelApplication",
          })
        );
      })
      .catch((error) => {
        console.log("Error cancelling ", error);
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to cancel Application",
            message:
              "There was an error cancelling the application. Please try again.",
            origin: "cancelApplication",
          })
        );
      });
  };
};

export const saveAddendumAnswers = (
  applicationId: number,
  addendumAnswers: AddendumsPostAddendums[]
): AppThunk => {
  const { baseRoute, apiKey } = getConfigs();
  const route = `${baseRoute}/applications/${applicationId}/addendums`;
  const accessToken = sessionStorage.getItem("access_token") || "";

  return (dispatch) => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("x-access-token", accessToken);
    headers.append("x-api-key", apiKey ?? "");
    const data: AddendumsPost = {
      id: applicationId,
      forms: addendumAnswers,
    };
    const options = {
      method,
      headers,
      body: JSON.stringify(data),
    };

    fetch(route, options)
      .then((response) => response.json())
      .then(() => {
        const { application: applicationFromLocal } =
          LocalStorageService.getApplication(applicationId);

        dispatch(
          setNotification({
            type: "INFO",
            message: "Success! Lease addendum answers submitted",
            origin: "saveAddendumAnswers",
          })
        );

        const newAddendums = mergeAddendumFormsAndResponses(
          applicationFromLocal.addendums,
          addendumAnswers
        );
        const newApplicationData = {
          ...(applicationFromLocal ?? {}),
          addendums: newAddendums,
        };

        dispatch(setApplication(newApplicationData));
        LocalStorageService.setApplicationInLocal(newApplicationData, null);
        dispatch(getApplicationById(applicationId));
      })
      .catch((error) => {
        console.log("Error saving addendum answers ", error);
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to save addendum answers",
            message: "Error trying to save Lease addendum answers",
          })
        );
      });
  };
};

export const completeAndRemoveApplication = (
  applicationId,
  unitId
): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();
  const route = `${baseRoute}/complete-and-remove`;
  return (dispatch) => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", sessionStorage.getItem("token") || "");
    headers.append("x-api-key", apiKey || "");
    const body = {
      applicationId: applicationId,
    };
    const options = {
      method,
      headers,
      body: JSON.stringify(body),
    };

    fetch(route, options)
      .then((data) => data.json())
      .then((res) => {
        if (!res.error) {
          dispatch(
            setNotification({
              type: "INFO",
              message: `Application for Unit ${unitId} has been successfully finished`,
            })
          );
        } else {
          dispatch(
            setNotification({
              type: "ERROR",
              title: "Unable to complete Application",
              message:
                "There was an error completing the application.\nPlease try again.",
            })
          );
        }
      })
      .catch((error) => {
        console.log("Error on completing application ", error);
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to complete Application",
            message:
              "There was an error completing the application.\nPlease try again.",
          })
        );
      });
  };
};

export const submitCreditScreening = (
  applicationId,
  applicantId,
  applicantName,
  applicant?: Applicant,
  applicationConfiguration?: ApplicationConfiguration | null
): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();
  const screeningVendor = getScreeningVendor(
    applicant,
    applicationConfiguration
  );
  const route =
    screeningVendor === ScreeningVendor.TRANSUNION
      ? `${baseRoute}/run-credit-screening/${applicantId}`
      : `${baseRoute}/run-fadv-screening`;
  return (dispatch) => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", sessionStorage.getItem("token") || "");
    headers.append("x-api-key", apiKey || "");
    headers.append(
      "x-access-token",
      sessionStorage.getItem("access_token") || ""
    );
    if (screeningVendor === ScreeningVendor.TRANSUNION) {
      headers.append(
        "x-id-token",
        fetchIdToken()?.replace("Bearer ", "") || ""
      );
    }
    const body: { applicationId: string | number; applicantId?: number } = {
      applicationId:
        screeningVendor === ScreeningVendor.TRANSUNION
          ? applicationId.toString()
          : applicationId,
    };
    if (screeningVendor === ScreeningVendor.FADV) {
      body.applicantId = applicantId;
    }
    const options = {
      method,
      headers,
      body: JSON.stringify(body),
    };
    const onErrorInstructions =
      "Please click view/edit application, check each applicant's entered address and make any necessary edits, then click submit again.";
    const timeoutRetryInstructions =
      "The screening request timed out before submit completed. It may still complete asynchronously. If the status does not update, click Submit again.";
    const conciseTimeoutRetryInstructions =
      "If the status does not update, click Submit again.";

    const isRecord = (value: unknown): value is Record<string, unknown> =>
      typeof value === "object" && value !== null;

    const getString = (value: unknown): string | undefined => {
      if (typeof value !== "string") return undefined;

      const trimmedValue = value.trim();
      return trimmedValue ? trimmedValue : undefined;
    };

    const getLegacyErrorDescription = (errors: unknown): string | undefined => {
      if (!isRecord(errors)) {
        return undefined;
      }

      const badRequestErrors = errors["Bad Request"];
      if (!Array.isArray(badRequestErrors) || !badRequestErrors[0]) {
        return undefined;
      }

      const firstBadRequestError = badRequestErrors[0];
      if (typeof firstBadRequestError !== "string") {
        return undefined;
      }

      try {
        const parsed = JSON.parse(firstBadRequestError);
        return getString(parsed?.ErrorDescription);
      } catch {
        return undefined;
      }
    };

    fetch(route, options)
      .then((res) => {
        if (!res.ok) {
          // Surface the HTTP error status before attempting to parse body.
          // This prevents a 4xx/5xx body that happens to lack error/errors fields
          // from being misread as a successful enqueue (consistent with pollCreditScreeningStatus).
          return res
            .json()
            .catch(() => ({}))
            .then((body) => {
              throw Object.assign(
                new Error(body?.message || `HTTP ${res.status}`),
                { status: res.status, body }
              );
            });
        }
        return res.json();
      })
      .then((res) => {
        // AP2-6117: SharedServices POST /credit now returns HTTP 202 with { status: "accepted", message }
        // on enqueue success. The sync response no longer contains errors[] or screenings[].
        // Screening outcomes (including TU failures) are delivered asynchronously via EventBridge.
        //
        // Success condition: res.status === "accepted" for TU, or an async-timeout TU pending ACK,
        // or no error/errors field for FADV.
        const responseMessage = getString(res?.message);
        const responseErrors = res?.errors;
        const responseError = res?.error;

        const vendorErrorMessage = Array.isArray(responseErrors)
          ? getString(responseErrors[0]?.message) ||
            getString(responseErrors[0]?.detail)
          : undefined;

        const timeoutPattern =
          /(time\s?out|timed\s?out|gateway timeout|\b504\b)/i;
        const hasTimeoutSignalFromTopLevel = timeoutPattern.test(
          [responseMessage, getString(responseError)].filter(Boolean).join(" ")
        );
        const hasTimeoutSignalFromVendor =
          !!vendorErrorMessage && timeoutPattern.test(vendorErrorMessage);
        const hasTimeoutSignal =
          hasTimeoutSignalFromTopLevel || hasTimeoutSignalFromVendor;
        const hasAsyncContinuationSignal = /(asynchron|continue)/i.test(
          [responseMessage, getString(responseError)].filter(Boolean).join(" ")
        );
        const isTuAsyncTimeoutAck =
          screeningVendor === ScreeningVendor.TRANSUNION &&
          String(res?.status || "").toLowerCase() === "pending" &&
          hasTimeoutSignalFromTopLevel &&
          hasAsyncContinuationSignal;

        const isSuccess =
          screeningVendor === ScreeningVendor.TRANSUNION
            ? res?.status === "accepted" || isTuAsyncTimeoutAck
            : !res.errors && !res.error;
        if (isSuccess) {
          if (screeningVendor === ScreeningVendor.TRANSUNION) {
            // TU: 202 ACK - modal will poll via pollCreditScreeningStatus.
            // Use origin "tu-enqueue-ack" so the modal can distinguish this from
            // a terminal success notification and start polling instead of closing.
            const inProgressMessage = `${screeningVendor} Credit Screening for ${applicantName} is in progress`;
            const tuAckMessage =
              isTuAsyncTimeoutAck && responseMessage
                ? `${inProgressMessage}\n\n${responseMessage}`
                : inProgressMessage;
            dispatch(
              setNotification({
                type: "INFO",
                message: tuAckMessage,
                origin: "tu-enqueue-ack",
              })
            );
          } else {
            dispatch(
              setNotification({
                type: "INFO",
                message: `${screeningVendor} Credit Screening for ${applicantName} is in progress`,
              })
            );
          }
        } else {
          const timeoutGuidance =
            screeningVendor === ScreeningVendor.TRANSUNION && hasTimeoutSignal
              ? hasTimeoutSignalFromTopLevel
                ? conciseTimeoutRetryInstructions
                : timeoutRetryInstructions
              : undefined;
          const legacyErrorDescription =
            getLegacyErrorDescription(responseErrors);

          const message =
            screeningVendor === ScreeningVendor.TRANSUNION
              ? [
                  responseMessage,
                  timeoutGuidance,
                  onErrorInstructions,
                  vendorErrorMessage
                    ? `Details from screening service: ${vendorErrorMessage}`
                    : undefined,
                ]
                  .filter(Boolean)
                  .join("\n\n")
              : `${onErrorInstructions}${
                  legacyErrorDescription
                    ? `\n\nDetails from screening service: ${legacyErrorDescription}`
                    : ""
                }`;

          dispatch(
            setNotification({
              type: "ERROR",
              title: "Unable to run credit screening",
              message: message,
            })
          );
        }
      })
      .catch((error) => {
        console.log("Error credit screening ", error);
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to run credit screening",
            message: onErrorInstructions,
          })
        );
      });
  };
};

/**
 * AP2-6117: Polls GET /get-credit-screening/{applicantId} every 2 seconds until
 * any screening record is created for the applicant (indicating the async worker
 * has created a record), polling exceeds 180 seconds, or the poll API errors.
 *
 * Returns a cleanup function that the caller (SubmitCreditScreeningModal) should
 * invoke on unmount to prevent state updates after the component is gone.
 *
 * Stop conditions:
 *   - Any record returned for the applicant -> success INFO notification, close modal
 *   - Screening record with Error status -> error notification, keep modal open for retry
 *   - Polling timeout (180s) -> timeout error notification
 *   - Fetch error -> error notification
 *
 * FADV flow is unaffected - this thunk is only dispatched for TU communities.
 */
export const pollCreditScreeningStatus = (
  applicantId: number,
  applicantName: string,
  screeningVendor: ScreeningVendor.FADV | ScreeningVendor.TRANSUNION
): AppThunk<() => void> => {
  return (dispatch) => {
    const { apiKey, baseRoute } = getConfigs();
    const POLL_INTERVAL_MS = 2000;
    const POLL_TIMEOUT_MS = 180_000;
    const startTime = Date.now();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const cleanup = () => {
      cancelled = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const schedulePoll = () => {
      if (cancelled) return;

      if (Date.now() - startTime >= POLL_TIMEOUT_MS) {
        cleanup();
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to run credit screening",
            message: "Credit screening submission timed out. Please try again.",
          })
        );
        return;
      }

      const poll = async () => {
        if (cancelled) return;

        try {
          const headers = new Headers({
            "Content-type": "application/json",
            Accept: "application/json",
          });
          headers.append(
            "Authorization",
            sessionStorage.getItem("token") || ""
          );
          headers.append("x-api-key", apiKey || "");
          headers.append(
            "x-access-token",
            sessionStorage.getItem("access_token") || ""
          );

          const res = await fetch(
            `${baseRoute}/get-credit-screening/${applicantId}`,
            { method: "GET", headers }
          );

          if (cancelled) return;

          if (!res.ok) {
            cleanup();
            dispatch(
              setNotification({
                type: "ERROR",
                title: "Unable to run credit screening",
                message:
                  "Credit screening submission failed. Click Submit to retry.",
              })
            );
            return;
          }

          const data = await res.json();
          if (cancelled) return;

          // data is expected to be an array of ApplicantScreening records.
          // Any non-empty result means the async worker has created the record.
          const screenings: { screeningStatus?: string }[] = Array.isArray(data)
            ? data
            : data?.applicantScreenings ?? [];

          if (screenings.length === 0) {
            // Worker has not yet created the record - schedule next poll
            if (!cancelled) {
              timeoutId = setTimeout(schedulePoll, POLL_INTERVAL_MS);
            }
            return;
          }

          cleanup();

          // Stop condition: any non-empty result (including Pending records) means the
          // async worker has run and created a DB record for this applicant. The modal's
          // job is to confirm the worker ran, not to wait for the final TU decision.
          // Final outcomes (Approved/Declined/etc.) are delivered via EventBridge/webhook.
          // Only Error status (worker-detected failure) keeps the modal open for retry.
          const hasError = screenings.some(
            (s) => s.screeningStatus?.toLowerCase() === "error"
          );

          if (hasError) {
            dispatch(
              setNotification({
                type: "ERROR",
                title: "Unable to run credit screening",
                message:
                  "Credit screening submission failed. Click Submit to retry.",
              })
            );
          } else {
            window.localStorage.setItem(
              "REFRESH_APPS",
              Math.random().toString()
            );
            dispatch(
              setNotification({
                type: "INFO",
                message: `${screeningVendor} Credit Screening for ${applicantName} is in progress`,
              })
            );
          }
        } catch (err) {
          if (cancelled) return;
          console.log("Error polling credit screening status", err);
          cleanup();
          dispatch(
            setNotification({
              type: "ERROR",
              title: "Unable to run credit screening",
              message:
                "Credit screening submission failed. Click Submit to retry.",
            })
          );
        }
      };

      poll();
    };

    schedulePoll();

    return cleanup;
  };
};

export const getScreeningVendor = (
  applicant?: Applicant | null,
  applicationConfiguration?: ApplicationConfiguration | null
): ScreeningVendor.FADV | ScreeningVendor.TRANSUNION => {
  const configuredCredit = applicationConfiguration?.screening?.credit;
  if (configuredCredit === true) {
    return ScreeningVendor.TRANSUNION;
  }
  if (configuredCredit === false) {
    return ScreeningVendor.FADV;
  }

  const hasTransUnionScreening = applicant?.applicantScreening?.some(
    (screening) =>
      String(screening.screeningVendor || "")
        .toLowerCase()
        .includes("transunion")
  );

  return hasTransUnionScreening
    ? ScreeningVendor.TRANSUNION
    : ScreeningVendor.FADV;
};

export const confirmDeposit = (applicationId, applicantName): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();
  const route = `${baseRoute}/application/${applicationId}/confirm-deposit-paid`;
  return (dispatch) => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", sessionStorage.getItem("token") || "");
    headers.append("x-api-key", apiKey || "");
    const body = {
      applicationId: applicationId,
    };
    const options = {
      method,
      headers,
      body: JSON.stringify(body),
    };
    fetch(route, options)
      .then((data) => data.json())
      .then((res) => {
        if (!res.errors && !res.error) {
          dispatch(
            setNotification({
              type: "INFO",
              message: `Condition Met, increased security deposit met for ${
                applicantName || "applicant"
              }, Application ${applicationId}`,
            })
          );
        } else {
          dispatch(
            setNotification({
              type: "ERROR",
              title: "Unable to confirm increased deposit paid",
              message:
                "There was an error confirming increased deposit paid.\nPlease try again.",
            })
          );
        }
      })
      .catch((error) => {
        console.log("Error confirming ", error);
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to confirm increased deposit paid",
            message:
              "There was an error confirming increased deposit paid.\nPlease try again.",
          })
        );
      });
  };
};

export const additionalDeposit = (applicationId, applicantName): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();
  const route = `${baseRoute}/applications/${applicationId}/additional-deposit`;
  const accessToken = sessionStorage.getItem("access_token") || "";
  return (dispatch) => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("x-access-token", accessToken);
    headers.append("x-api-key", apiKey || "");
    const body = {
      applicationId: applicationId,
    };
    const options = {
      method,
      headers,
      body: JSON.stringify(body),
    };
    fetch(route, options)
      .then((data) => data.json())
      .then((res) => {
        if (!res.errors && !res.error) {
          dispatch(
            setNotification({
              type: "INFO",
              message: `Condition Met, increased security deposit met for ${
                applicantName || "applicant"
              }, Application ${applicationId}`,
            })
          );
        } else {
          dispatch(
            setNotification({
              type: "ERROR",
              title: "Unable to confirm increased deposit paid",
              message:
                "There was an error confirming increased deposit paid.\nPlease try again.",
            })
          );
        }
      })
      .catch((error) => {
        console.log("Error confirming ", error);
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to confirm increased deposit paid",
            message:
              "There was an error confirming increased deposit paid.\nPlease try again.",
          })
        );
      });
  };
};

export const updateApplicantById = (applicantId, applicantData): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();

  const route = `${baseRoute}/applicants/${applicantId}`;

  return (dispatch) => {
    const method = "PUT";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", fetchIdToken() || "");
    headers.append("x-api-key", apiKey || "");
    const body = {
      firstName: applicantData.firstName,
      middleName: applicantData.middleName,
      lastName: applicantData.lastName,
      dob: applicantData.dob,
      address1: applicantData.address1,
      address2: applicantData.address2,
      city: applicantData.city,
      state: applicantData.state,
      zip: applicantData.zip,
      emailAddress: applicantData.emailAddress,
      nonUSPhone: applicantData.nonUSPhone,
      isSSN: applicantData.isSSN,
      smsPreference: applicantData.smsPreference,
      emailPreference: applicantData.emailPreference,
      isMiddleName: applicantData.isMiddleName,
      phoneNumberType: applicantData.phoneNumberType,
      phoneNumber: applicantData.phoneNumber,
    };
    const options = {
      method,
      headers,
      body: JSON.stringify(body),
    };
    fetch(route, options)
      .then((data) => data.json())
      .then((res) => {
        if (res.status === 400) {
          dispatch(
            setNotification({
              type: "VALIDATION_ERROR",
              title: "Update Applicant Info",
              message: JSON.stringify(res.errors),
            })
          );
          return;
        }

        dispatch(
          setNotification({
            type: "INFO",
            title: "Update Applicant Info",
            message: `Applicant Info for applicant ${applicantId} has been successfully updated`,
          })
        );
        return res;
      })
      .catch((error) => {
        console.log(
          `Error on updating applicant info for applicant - ${applicantId}`,
          error
        );
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Update Applicant Info",
            message: "Unable to update applicant information",
          })
        );
      });
  };
};

export const postApplicationHoldDateFromNow = (
  applicationId,
  minutes
): AppThunk => {
  const { baseRoute, apiKey } = getConfigs();

  const route = `${baseRoute}/applications/${applicationId}/holdfromnow?minutes=${minutes}`;

  return (dispatch) => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
      Accept: "application/json",
      "x-id-token": fetchIdToken()?.replace("Bearer ", "") || "",
      "x-api-key": apiKey || "",
    });
    const options = {
      method,
      headers,
    };
    fetch(route, options)
      .then((data) => data.json())
      .then(() => {
        dispatch(
          setNotification({
            type: "INFO",
            message: `Hold Clock for Application ${applicationId} has been successfully reset`,
          })
        );
      })
      .catch((error) => {
        console.log("error on postApplicationHoldDateFromNow ", error);
        dispatch(
          setNotification({
            type: "ERROR",
            title: `Unable to reset Hold Clock`,
            message: `There was an error reseting Hold Clock for Application ${applicationId}.\nPlease try again.`,
          })
        );
      });
  };
};

export const postLease = (
  applicationId,
  leaseEventId,
  previousLegalRent?
): AppThunk => {
  const { baseRoute, apiKey } = getConfigs();
  const route = `${baseRoute}/applications/${applicationId}/lease`;

  return (dispatch) => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
      Accept: "application/json",
      "x-access-token": fetchAccessToken() || "",
      "x-api-key": apiKey || "",
    });
    let body;
    if (previousLegalRent) {
      body = JSON.stringify({
        applicationId,
        leaseEventId,
        previousLegalRent,
      });
    } else {
      body = JSON.stringify({
        applicationId,
        leaseEventId,
      });
    }

    const options = {
      method,
      headers,
      body,
    };

    return fetch(route, options)
      .then((data) => data.json())
      .then((data: PostLeaseResponse) => {
        if (leaseEventId === LeaseEventId.LeaseGenerated) {
          dispatch(
            setNotification({
              type: "INFO",
              message: `Lease has been generated and successfully sent out`,
              origin: "postLease",
            })
          );
        } else if (leaseEventId === LeaseEventId.LeaseResent) {
          dispatch(
            setNotification({
              type: "INFO",
              message: `Lease has been successfully sent out`,
              origin: "postLease",
            })
          );
        }

        const { application: applicationFromLocal } =
          LocalStorageService.getApplication(applicationId);

        const newApplicationData = {
          ...(applicationFromLocal ?? {}),
          lease: {
            ...(applicationFromLocal?.lease || {}),
            leaseEvents: [
              data,
              ...(applicationFromLocal?.lease?.leaseEvents || []),
            ],
          },
        };

        dispatch(setApplication(newApplicationData));
        LocalStorageService.setApplicationInLocal(newApplicationData, null);
        dispatch(getApplicationById(applicationId));
      })
      .catch((error) => {
        if (leaseEventId === LeaseEventId.LeaseGenerated) {
          dispatch(
            setNotification({
              type: "ERROR",
              message: `Error generating lease, please try again`,
              origin: "postLease",
            })
          );
        } else if (leaseEventId === LeaseEventId.LeaseResent) {
          dispatch(
            setNotification({
              type: "ERROR",
              message: `Error sending out lease, please try again`,
              origin: "postLease",
            })
          );
        }
        console.log("error on postLease ", error);
      });
  };
};

export const getConfigurations = (): AppThunk => {
  const { configurationsBaseRoute, configurationApiKey } = getConfigs();

  return (dispatch) => {
    const route = `${configurationsBaseRoute}`;
    const method = "GET";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("x-api-key", configurationApiKey || "");
    headers.append("x-access-token", fetchAccessToken() || "");
    const options = {
      method,
      headers,
    };
    fetch(route, options)
      .then((data) => data.json())
      .then((res) => {
        dispatch(setConfiguration(res));
      })
      .catch((error) => {
        console.log("Error retrieving configuration", error);
      });
  };
};

export const getCommsPreferencesByApplicantIds = (
  applicantIds: string[]
): AppThunk => {
  return async (dispatch) => {
    try {
      const { apiKey, baseRoute } = getConfigs();
      const accessToken = sessionStorage.getItem("access_token") || "";

      const headers = new Headers({
        "Content-type": "application/json",
        Accept: "application/json",
        "x-api-key": apiKey || "",
        "x-access-token": accessToken,
      });

      const requests = applicantIds?.map(async (id) => {
        const route = `${baseRoute}/applicants/${id}/communication`;
        const res = await fetch(route, { method: "GET", headers });
        const data = await res.json();
        return {
          applicantId: id,
          email: data.customer?.email ?? null,
          sms: data.customer?.sms ?? null,
          emailPreference: data.preferences?.email ?? false,
          smsPreference: data.preferences?.sms ?? false,
        };
      });

      const results = await Promise.allSettled(requests);
      const preferencesMap: Record<string, CommsPreference> = {};
      results.forEach((result) => {
        if (result?.status === "fulfilled") {
          const commsPreference = result?.value;
          preferencesMap[commsPreference.applicantId] = commsPreference;
        }
      });

      dispatch(setCommsPreferences(preferencesMap));
    } catch (error) {
      console.error("Error fetching comms preferences:", error);
      dispatch(setCommsPreferences({}));
    }
  };
};

export const resolveApplicantScreeningIssue = (
  applicantId: string,
  issueId: number
): AppThunk<Promise<boolean>> => {
  return async (dispatch) => {
    try {
      const { apiKey, baseRoute } = getConfigs();
      const accessToken = sessionStorage.getItem("access_token") || "";

      const headers = new Headers({
        "Content-Type": "application/json",
        Accept: "application/json",
        "x-api-key": apiKey || "",
        "x-access-token": accessToken,
      });

      const url = `${baseRoute}/applicants/${applicantId}/screening-issues/${issueId}`;

      const res = await fetch(url, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ resolved: true }),
      });

      if (!res.ok) {
        console.error("Failed to resolve issue. Status:", res.status);
        return false;
      }

      dispatch(resolveIssueSuccess({ applicantId, issueId }));
      return true;
    } catch (error) {
      console.error("Error resolving screening issue:", error);
      return false;
    }
  };
};

export const clearCustomerRisk = (
  highRiskApplicants: Applicant[],
  applicationId: number
): AppThunk<Promise<boolean>> => {
  return async (dispatch) => {
    try {
      const { apiKey, baseRoute } = getConfigs();
      const accessToken = sessionStorage.getItem("access_token") || "";

      // Get the current logged-in user from the auth token
      const token = fetchIdToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const cleanToken = token.replace(/^Bearer\s+/, "");
      const decodedToken = decodeToken(cleanToken);

      // Extract user ID (try multiple possible fields)
      const clearedBy = decodedToken["cognito:username"] || decodedToken.name;

      if (!clearedBy) {
        throw new Error("Could not extract user ID from token");
      }

      // Extract applicant IDs
      const clearedApplicantIds = highRiskApplicants
        .map((applicant) => applicant.id)
        .filter((id) => id !== undefined);

      if (clearedApplicantIds.length === 0) {
        console.error("No valid applicant IDs to clear");
        return false;
      }

      const headers = new Headers({
        "Content-Type": "application/json",
        Accept: "application/json",
        "x-api-key": apiKey || "",
        "x-access-token": accessToken,
      });
      const url = `${baseRoute}/applicants/customerrisk`;

      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          clearedBy,
          clearedApplicationId: applicationId,
          clearedApplicantIds,
        }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error(
          "Failed to clear customer risk. Status:",
          res.status,
          errorText
        );

        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to approve high-risk applicant",
            message:
              "There was an error clearing the high-risk flag. Please try again.",
            origin: "clearCustomerRisk",
          })
        );
        return false;
      }

      // Show success notification
      dispatch(
        setNotification({
          type: "INFO",
          message:
            "High-risk applicant(s) approved successfully. Risk clearance has been recorded.",
          origin: "clearCustomerRisk",
        })
      );

      // Refresh application data from backend (this will update Redux and localStorage automatically)
      await dispatch(getApplicationById(applicationId));

      return true;
    } catch (error) {
      console.error("Error clearing customer risk:", error);
      dispatch(
        setNotification({
          type: "ERROR",
          title: "Unable to approve high-risk applicant",
          message: "An unexpected error occurred. Please try again.",
          origin: "clearCustomerRisk",
        })
      );
      return false;
    }
  };
};

const isCommunityFeatureEnabled = (
  communityArray: string[] | null | undefined,
  communityId: string
) =>
  (communityArray?.length === 0 || communityArray?.includes(communityId)) ??
  false;

export const getIsIncSecDepositFeatureEnabled = (
  features: Features,
  communityId: string
): boolean =>
  isCommunityFeatureEnabled(features?.payment?.communities, communityId) &&
  isCommunityFeatureEnabled(
    features?.payment?.increasedSecurityDeposit,
    communityId
  );

export const getIsSecDepositFeatureEnabled = (
  features: Features,
  communityId: string
): boolean =>
  isCommunityFeatureEnabled(features?.payment?.communities, communityId) &&
  isCommunityFeatureEnabled(features?.payment?.securityDeposit, communityId);

export const getIsDocumentScreeningFeatureEnabled = (
  features: Features,
  communityId: string,
  applicationConfig?: ApplicationConfiguration
): boolean => {
  const appConfigDocument = applicationConfig?.screening?.document;

  if (typeof appConfigDocument === "boolean") {
    return appConfigDocument;
  }

  return (
    isCommunityFeatureEnabled(features?.screening?.communities, communityId) &&
    isCommunityFeatureEnabled(features?.screening?.document, communityId)
  );
};

export const getIsCommunityIssueResolutionFeatureEnabled = (
  features: Features,
  communityId: string
): boolean =>
  isCommunityFeatureEnabled(features?.screening?.issueResolution, communityId);

export const getIsFurtherReviewFeatureEnabled = (
  features: Features,
  communityId: string
): boolean =>
  isCommunityFeatureEnabled(
    features?.customerRisk?.furtherReviewList,
    communityId
  );
